import { StarIcon } from 'lucide-react'
import { useState } from 'react'

import Card from 'apps/employer_renewals/components/card/Card'
import PageContainer from 'apps/employer_renewals/components/layout/PageContainer'
import NavigationButtons from 'apps/employer_renewals/components/NavigationButtons'
import useAppNavigation from 'apps/employer_renewals/hooks/useAppNavigation'
import { useAllowances } from 'apps/employer_renewals/providers/AllowancesProvider'
import { AssetOption, useEmployerRenewalAsset } from 'apps/employer_renewals/providers/EmployerRenewalAssetProvider'
import { EmployerRenewalsRoute } from 'apps/employer_renewals/routes'
import { Badge, Banner, Checkbox } from 'slab'
import { cn } from 'utils'

const marketplaceCategories: { name: string, options: { name: AssetOption, color: string }[] }[] = [
  {
    name: 'Fitness',
    options: [
      { name: AssetOption.Peloton, color: '#FFD9D1' },
      { name: AssetOption.Hyperice, color: '#D4D4D4' },
    ],
  },
  {
    name: 'Sleep',
    options: [
      { name: AssetOption.EightSleep, color: '#CCCCCC' },
      { name: AssetOption.Oura, color: '#D5DBE3' },
    ]
  },
  {
    name: 'Mental health',
    options: [
      { name: AssetOption.GrowTherapy, color: '#D7D7D7' },
      { name: AssetOption.Lyra, color: '#D0D8DD' },
    ]
  },
  {
    name: 'Preventative care',
    options: [
      { name: AssetOption.Function, color: '#F3E4DC' },
      { name: AssetOption.Prenuvo, color: '#CCD6D6' },
    ]
  },
  {
    name: 'Weight loss',
    options: [
      { name: AssetOption.HimsHers, color: '#D1D1D1' },
      { name: AssetOption.Trx, color: '#CCCCCC' },
    ]
  },
  {
    name: 'Telehealth',
    options: [
      { name: AssetOption.Sesame, color: '#DED3F5' },
    ]
  },
]

export default function QmePage () {
  const { shouldShowDependentCoveragePage, navigateTo } = useAppNavigation()
  const { getAssetPath } = useEmployerRenewalAsset()
  const {
    employerWillOfferQmeInRenewalYear,
    setEmployerWillOfferQmeInRenewalYear,
    saveAllowances,
    isSavingAllowances,
  } = useAllowances()

  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const saveAndNavigate = async () => {
    const isSaved = await saveAllowances()
    if (!isSaved) {
      setErrorMessage('An error occurred while saving your response. Please try again or contact support if the problem persists.')
      return
    }
    if (shouldShowDependentCoveragePage) {
      navigateTo(EmployerRenewalsRoute.ALLOWANCES_DEPENDENTS_COVERAGE)
    } else {
      navigateTo(EmployerRenewalsRoute.ALLOWANCES_CONFIRM)
    }
  }

  return (
    <PageContainer maxWidth='md'>
      <Card
        className='border-emphasis shadow-emphasis'
        style={{ background: 'linear-gradient(180deg, var(--color-primitive-thatch-200, #F1E2FF) -10.45%, var(--color-primitive-thatch-100, #F9F4FF) 12.98%, rgba(249, 244, 255, 0.00) 100%)' }}
        footer={
          <Checkbox
            label='Allow your employees to use leftover allowance on eligible items'
            description="This won't impact your estimated costs"
            checked={employerWillOfferQmeInRenewalYear}
            disabled={isSavingAllowances}
            onChange={e => {
              const checked = e.target.checked
              setEmployerWillOfferQmeInRenewalYear(checked)
            }}
          />
        }
      >
        <div className='flex flex-col gap-6'>

          <div className='flex items-center gap-3'>
            <div className='flex shadow-sm border border-violet-300 bg-violet-50 py-1 px-2 rounded-md text-violet-500 body-sm-bold'>Recommended</div>
            <h2 className='heading-sm text-main'>More flexibility for your employees</h2>
          </div>

          <div className='flex flex-col gap-3'>
            <div className='flex gap-3'>
              <Badge type='success' bordered leftIcon={StarIcon}>Increase employee satisfaction</Badge>
              <Badge type='success' bordered leftIcon={StarIcon}>Cover co-pays, prescriptions, lab tests, and more</Badge>
            </div>
            <p className='body-sm'>
              Allow your employees to use any leftover allowance on qualified medical expenses –– including co-pays, prescriptions, lab tests, and more –– to provide better benefits while still managing costs. You'll pay no more than your estimated total 2026 cost, and members can buy eligible items that contribute to their well-being.
            </p>
          </div>

          <div className='flex flex-col gap-2'>
            <h3 className='body-bold'>New: Thatch Marketplace</h3>
            <p className='body-sm'>
              Thatch marketplace gives your employees access to exclusive discounts on vetted products and services including:
            </p>
          </div>

          <div className='flex flex-wrap gap-2 items-center'>
            {marketplaceCategories.map((category) => (
              <div key={category.name} className='flex items-center gap-2 bg-white rounded-full border border-color-stroke-light px-2 py-1 w-fit'>
                <div className='flex items-center gap-2'>
                  {category.options.map(({ name, color }, idx) => (
                    <div
                      key={name}
                      className={cn('h-6 w-8 rounded-full p-1 flex items-center justify-center border border-white hover:scale-105 transition-all duration-200', idx > 0 && '-ml-[1rem]')}
                      style={{ backgroundColor: color }}
                    >
                      <img src={getAssetPath(name)} alt={name} className='h-full w-auto' />
                    </div>
                  ))}
                </div>
                <span className='body-sm text-main'>{category.name}</span>
              </div>
            ))}
            <span className='body-sm text-subdued'>+ more</span>
          </div>
        </div>
      </Card>
      {errorMessage && (
        <Banner type='critical' title='An error occurred'>
          {errorMessage}
        </Banner>
      )}
      <NavigationButtons
        nextButtonText='Next'
        isLoading={isSavingAllowances}
        onNext={saveAndNavigate}
      />
    </PageContainer>
  )
}
